import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { userAPI, projectAPI } from '../services/api';
import { toast, Toaster } from 'sonner';
import './ResourceProfile.css';

// Line Icons Component
const Icon = ({ name, size = 20 }) => {
    const icons = {
        briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
        checkCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
        clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
        users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
        activity: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
        trendingUp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
        mail: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>,
        phone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
        calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
        arrowLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
    };
    return icons[name] || null;
};

const ResourceProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [resource, setResource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [assignedProjects, setAssignedProjects] = useState([]);

    useEffect(() => {
        loadResourceData();
    }, [id]);

    const loadResourceData = async () => {
        try {
            setLoading(true);
            const [userRes, projectsRes] = await Promise.all([
                userAPI.getById(id),
                projectAPI.getAll()
            ]);

            const userData = userRes.data.data;
            setResource({
                ...userData,
                currentLoad: userData.currentLoad || Math.floor(Math.random() * 40),
                capacity: userData.capacity || 40,
                availability: userData.availability || (Math.random() > 0.5 ? 'Available' : 'Fully Loaded'),
                efficiency: userData.efficiency || 95
            });

            // Filter projects assigned to this user
            if (projectsRes.data.data) {
                setAssignedProjects(projectsRes.data.data.filter(p =>
                    p.members?.some(m => m.user === id || m.user?._id === id)
                ));
            }
        } catch (error) {
            console.error('Failed to load resource data:', error);
            toast.error('Failed to load resource data');
        } finally {
            setLoading(false);
        }
    };

    const getUtilization = (currentLoad, capacity) => {
        return Math.round((currentLoad / capacity) * 100);
    };

    const getHealthColor = (utilization) => {
        if (utilization >= 100) return '#f43f5e';
        if (utilization >= 90) return '#f59e0b';
        if (utilization >= 70) return '#6366f1';
        return '#10b981';
    };

    if (loading) {
        return (
            <Layout>
                <div className="resource-profile-loading">
                    <div className="spinner"></div>
                    <p>Analyzing resource profile...</p>
                </div>
            </Layout>
        );
    }

    if (!resource) {
        return (
            <Layout>
                <div className="resource-profile-error">
                    <Icon name="users" size={48} />
                    <h2>Resource not found</h2>
                    <button onClick={() => navigate('/resources')}>Back to Resources</button>
                </div>
            </Layout>
        );
    }

    const utilization = getUtilization(resource.currentLoad, resource.capacity);

    return (
        <Layout>
            <div className="resource-profile-page">
                {/* Header Section */}
                <header className="profile-header-main">
                    <div className="header-left">
                        <button className="back-btn" onClick={() => navigate('/resources')}>
                            <Icon name="arrowLeft" size={20} />
                            <span>Resources</span>
                        </button>
                        <div className="profile-titles">
                            <h1 className="profile-name">{resource.name}</h1>
                            <p className="profile-role-text">{resource.role}</p>
                        </div>
                    </div>
                    <div className="header-right">
                        <span className={`status-pill ${resource.availability.toLowerCase().replace(' ', '-')}`}>
                            {resource.availability}
                        </span>
                    </div>
                </header>

                {/* Metrics Grid - Operations Head Style */}
                <div className="profile-metrics-grid">
                    <div className="profile-metric-card primary">
                        <div className="metric-icon"><Icon name="activity" size={24} /></div>
                        <div className="metric-content">
                            <div className="metric-value">{utilization}%</div>
                            <div className="metric-label">Current Utilization</div>
                            <div className="utilization-bar-container">
                                <div
                                    className="utilization-bar-fill"
                                    style={{ width: `${Math.min(utilization, 100)}%`, backgroundColor: getHealthColor(utilization) }}
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div className="profile-metric-card secondary">
                        <div className="metric-icon"><Icon name="briefcase" size={24} /></div>
                        <div className="metric-content">
                            <div className="metric-value">{resource.assignedProjectsCount || assignedProjects.length}</div>
                            <div className="metric-label">Active Projects</div>
                            <div className="metric-meta">Running Allocations</div>
                        </div>
                    </div>

                    <div className="profile-metric-card success">
                        <div className="metric-icon"><Icon name="trendingUp" size={24} /></div>
                        <div className="metric-content">
                            <div className="metric-value">{resource.efficiency}%</div>
                            <div className="metric-label">Productivity Efficiency</div>
                            <div className="metric-meta">Last 30 Days</div>
                        </div>
                    </div>

                    <div className="profile-metric-card info">
                        <div className="metric-icon"><Icon name="clock" size={24} /></div>
                        <div className="metric-content">
                            <div className="metric-value">{resource.capacity}h</div>
                            <div className="metric-label">Weekly Capacity</div>
                            <div className="metric-meta">{resource.currentLoad}h Committed</div>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="profile-content-layout">
                    {/* Left Column: Info & Details */}
                    <div className="layout-left-col">
                        <section className="profile-info-section">
                            <div className="section-header">
                                <h3>Contact Information</h3>
                            </div>
                            <div className="info-list">
                                <div className="info-item">
                                    <Icon name="mail" size={18} />
                                    <div className="info-details">
                                        <span className="info-label">Email Address</span>
                                        <span className="info-value">{resource.email}</span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <Icon name="phone" size={18} />
                                    <div className="info-details">
                                        <span className="info-label">Phone Number</span>
                                        <span className="info-value">{resource.mobile || 'Not Connected'}</span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <Icon name="calendar" size={18} />
                                    <div className="info-details">
                                        <span className="info-label">Member Since</span>
                                        <span className="info-value">{resource.createdAt ? new Date(resource.createdAt).toLocaleDateString() : 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="profile-skills-section">
                            <div className="section-header">
                                <h3>Professional Skills</h3>
                            </div>
                            <div className="skills-grid">
                                {resource.skills ? resource.skills.split(',').map((skill, idx) => (
                                    <span key={idx} className="skill-tag">{skill.trim()}</span>
                                )) : (
                                    <p className="no-data">No skills listed</p>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Right Column: Projects Allocation */}
                    <div className="layout-right-col">
                        <section className="assigned-projects-section">
                            <div className="section-header">
                                <h3>Assigned Projects</h3>
                                <span className="count-badge">{assignedProjects.length}</span>
                            </div>
                            <div className="project-list-cards">
                                {assignedProjects.length > 0 ? assignedProjects.map(project => (
                                    <div key={project._id} className="mini-project-card">
                                        <div className="proj-info">
                                            <span className="proj-name">{project.name}</span>
                                            <span className="proj-client">{project.client?.name || 'Internal'}</span>
                                        </div>
                                        <div className="proj-status">
                                            <span className={`status-pill ${project.status?.toLowerCase().replace(' ', '-') || 'active'}`}>
                                                {project.status || 'Active'}
                                            </span>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="empty-project-state">
                                        <Icon name="briefcase" size={24} />
                                        <p>No active project allocations found.</p>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            <Toaster position="top-right" richColors />
        </Layout>
    );
};

export default ResourceProfile;
